import { put, select, takeEvery, getContext, call, throttle, putResolve } from 'redux-saga/effects';
import {
  toggleCamera,
  toggleMic,
  toggleScreenSharing,
  toggleScreenSharingSuccess,
  toggleCameraFacingMode,
  changeCameraFacingMode,
  setLocalStream,
  sendWebRTCNegotiate,
  webrtcNegotiate,
} from './actions';
import {
  sendCameraActivated,
  sendCameraDeactivated,
  sendMicrophoneUnmuted,
  sendMicrophoneMuted,
  sendDesktopSharingActivated,
  sendDesktopSharingDeactivated,
  sendChangeCameraFacingMode,
} from '../communication/actions';
import videoSelectors from './selectors';
import appSelectors from '../app/selectors';
import { getLogger } from '../../logger/index';
import {
  toggleScreanSharing,
  changeCameraFacingMode as changeCameraFacingModeWebRTC,
  toggleCamera as toggleCameraWebRTC,
  toggleMic as toggleMicWebRTC,
  peerNegotiate,
  exchangeLocalStream,
} from './kvsWebRTC';
import { APP_SET_ACTIVE } from '../app/actions';

const logger = getLogger('communicationVideo');

function* toggleCameraSaga() {
  try {
    const isEnabled = yield select(videoSelectors.isCameraEnabled);
    const socketArgs = yield select(videoSelectors.getSocketObj);
    const localStream = yield select(videoSelectors.getLocalStream);

    logger.debug('Toggle camera - WebRTC', isEnabled);
    toggleCameraWebRTC(localStream, isEnabled);

    logger.debug('Send toggle camera to remote calls', isEnabled);
    yield put(isEnabled ? sendCameraActivated(socketArgs) : sendCameraDeactivated(socketArgs));
  } catch (e) {
    logger.error(e);
  }
}

function* toggleMicSaga() {
  try {
    const isMuted = yield select(videoSelectors.isMicMuted);
    const isEnabled = !isMuted;
    const socketArgs = yield select(videoSelectors.getSocketObj);
    const localStream = yield select(videoSelectors.getLocalStream);

    logger.debug('Toggle mic - WebRTC', isEnabled);
    toggleMicWebRTC(localStream, isEnabled);

    logger.debug('Send toggle mic to remote calls', isEnabled);
    yield put(isEnabled ? sendMicrophoneUnmuted(socketArgs) : sendMicrophoneMuted(socketArgs));
  } catch (e) {
    logger.error(e);
  }
}

function* toggleScreanSharingSaga() {
  try {
    const isEnabled = yield select(videoSelectors.isScreanSharingEnabled);
    const socketArgs = yield select(videoSelectors.getSocketObj);
    const localStream = yield select(videoSelectors.getLocalStream);
    const cameraFacingMode = yield select(videoSelectors.getCameraFacingMode);
    const mediaDevices = yield getContext('mediaDevices');

    logger.debug('Toggle screanSharing - WebRTC', isEnabled);
    const success = yield toggleScreanSharing(localStream, cameraFacingMode, !isEnabled, mediaDevices);

    if (success) {
      yield put(toggleScreenSharingSuccess(!isEnabled));
      logger.debug('Send toggle screanSharing to remote calls', !isEnabled);
      yield put(!isEnabled ? sendDesktopSharingActivated(socketArgs) : sendDesktopSharingDeactivated(socketArgs));
    }
  } catch (e) {
    logger.error(e);
  }
}

function* changeCameraFacingModeSaga() {
  try {
    const socketArgs = yield select(videoSelectors.getSocketObj);
    const localStream = yield select(videoSelectors.getLocalStream);
    const facingMode = yield select(videoSelectors.getCameraFacingMode);
    const mediaDevices = yield getContext('mediaDevices');

    logger.debug('Toggle camera facing mode - WebRTC', facingMode);
    changeCameraFacingModeWebRTC(localStream, facingMode, mediaDevices);

    logger.debug('Send change camera facing mode to remote calls', facingMode);
    yield put(sendChangeCameraFacingMode({ ...socketArgs, facingMode }));
  } catch (e) {
    logger.error(e);
  }
}

function* appActivationChangeSaga() {
  try {
    const isActive = yield select(appSelectors.isActive);
    const isInCall = yield select(videoSelectors.isInCall);
    const hasVideo = yield select(videoSelectors.hasStreamWithVideo);
    const type = yield select(videoSelectors.getType);
    const localStream = yield select(videoSelectors.getLocalStream);

    logger.debug('App activation change. type:', type, 'hasVideo:', hasVideo, 'isActive:', isActive);
    if (isInCall && localStream && type === 'video' && isActive) {
      const isIncommingCall = yield select(videoSelectors.isIncommingCall);
      const remoteUserRole = yield select(videoSelectors.getFirstRemoteCallUserRole);

      if (isIncommingCall && remoteUserRole !== 'customer') {
        logger.debug('Exchange the local stream to new one');
        yield call(exchangeLocalStreamSaga);

        console.log('Renegotiate connection with new stream');
        yield call(peerNegotiate, true);
      } else {
        const toId = yield select(videoSelectors.getFirstRemoteCallUserId);
        if (!toId) {
          logger.error('No target user to negotiate peer connection');
        }
        logger.debug('Exchange the local stream to new one');
        yield call(exchangeLocalStreamSaga);

        logger.debug('Send RTCNegotiate to', toId);
        yield put(sendWebRTCNegotiate(toId));
      }
    }
  } catch (e) {
    logger.error(e);
  }
}

function* exchangeLocalStreamSaga() {
  const mediaDevices = yield getContext('mediaDevices');
  const localStream = yield select(videoSelectors.getLocalStream);
  const newStream = yield call(exchangeLocalStream, 'video', localStream, mediaDevices);

  logger.debug('Enable video after app activation');
  yield put(setLocalStream(newStream));

  logger.debug('Update mic state in the new stream');
  const isMicMuted = yield select(videoSelectors.isMicMuted);
  toggleMicWebRTC(newStream, !isMicMuted);

  logger.debug('Update camera state in the new stream');
  const isCameraEnabled = yield select(videoSelectors.isCameraEnabled);
  toggleCamera(newStream, isCameraEnabled);
}

function* peerRenegotiateSaga() {
  try {
    const isInCall = yield select(videoSelectors.isInCall);
    if (!isInCall) {
      return;
    }
    // const type = yield select(videoSelectors.getType);
    logger.debug('Trigger peer renegotiate');
    yield call(peerNegotiate, true);
  } catch (e) {
    logger.error(e);
  }
}

export default function* watchCallChangeSaga() {
  logger.debug('Watch toggle camera action');
  yield takeEvery(toggleCamera, toggleCameraSaga);
  logger.debug('Watch toggle mic action');
  yield takeEvery(toggleMic, toggleMicSaga);
  logger.debug('Watch toggle screanSharing action');
  yield takeEvery(toggleScreenSharing, toggleScreanSharingSaga);
  logger.debug('Watch change camera facing mode action');
  yield takeEvery([toggleCameraFacingMode, changeCameraFacingMode], changeCameraFacingModeSaga);
  logger.debug('Watch app activation change');
  yield throttle(3000, APP_SET_ACTIVE, appActivationChangeSaga);
  logger.debug('Watch WebRTC negotiation request');
  yield takeEvery(
    action => action.type === 'SOCKET_EVENT' && action.payload.eventName === 'webrtc_negotiate',
    peerRenegotiateSaga
  );
}
